<template>
  <div class="p-6">
    <!-- Filters -->
    <div class="grid md:grid-cols-4 gap-4 mb-6">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Tech</label>
        <select v-model="filters.userId" @change="changeTech" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          <option value="" disabled selected>Select Tech</option>
          <option value="All" v-if="userroles.some(r => [10, 20, 30, 40].includes(r))">All</option>
          <template v-for="(techList, deptName) in groupedTechnicians" :key="deptName">
            <optgroup v-if="deptName" :label="deptName">
              <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                {{ tech.name }}
              </option>
            </optgroup>
            <template v-else>
              <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                {{ tech.name }}
              </option>
            </template>
          </template>
        </select>
      </div>
      <div v-click-outside="closeClientDropdown">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mr-4">Client</label>
        <div class="relative">
          <input type="text"
          @focus="dropdownState.client.visible = true"
          @keydown.down.prevent="onKeyDown('client', 'down')"
          @keydown.up.prevent="onKeyDown('client', 'up')"
          @keydown.enter.prevent="onEnter('client', setClient, 'ticketTypeInput')"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
          placeholder="Client Name" v-model="companyname" />
          <button v-if="filters.companyId != '' && companyname !== ''" @click="clearClient"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <ul v-if="dropdownState.client.visible" data-field="client"
          class="absolute z-20 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
          <li v-for="(client, index) in filteredClients"
            :key="client.id"
            @click="setClient(client)"
            :class="[
              'cursor-pointer p-3',
              dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            ]"
          >
            {{ client.companyname }}
          </li>
        </ul>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Start Date</label>
        <input type="date" v-model="filters.startDate" @change="fetchJobs" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">End Date</label>
        <input type="date" v-model="filters.endDate" @change="fetchJobs" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
      </div>
    </div>

    <div class="flex justify-center gap-4 mb-4">
      <button class="bg-blue-500 text-white px-4 py-2 rounded" @click="changeDay('Prev')">Prev</button>
      <button
        :class="[
          'px-4 py-2 rounded text-white',
          disableNext ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
        ]"
        :disabled="disableNext"
        @click="changeDay('Next')"
      >
        Next
      </button>
    </div>

    <!-- Job Table -->
    <div class="shadow rounded-lg bg-white dark:bg-gray-800 max-h-[600px] overflow-y-auto relative">
      <table class="w-full text-sm text-left text-gray-500 dark:text-gray-200">
        <thead class="text-xs text-gray-700 uppercase bg-gray-200 dark:bg-gray-900 dark:text-gray-200 sticky top-0 z-10">
          <tr>
            <th scope="col" class="px-4 py-3 cursor-pointer w-[5%]" @click="sortJobs('id')">ID
              <span :class="getSortClass('id')"></span>
            </th>
            <th class="px-4 py-3 w-[10%] text-center">Activity</th>
            <th scope="col" class="px-4 py-3 cursor-pointer w-[10%]" @click="sortJobs('client')">Client
              <span :class="getSortClass('client')"></span>
            </th>
            <th scope="col" class="px-4 py-3 w-[10%]">Requester
            </th>
            <th scope="col" class="px-4 py-3 w-[10%]">Ticket ID
            </th>
            <th v-if="filters.userId == 'All'" scope="col" class="px-4 py-3 cursor-pointer w-[10%] text-center" @click="sortJobs('tech')">Tech
              <span :class="getSortClass('tech')"></span>
            </th>
            <th scope="col" class="px-4 py-3 w-[20%] text-center">Description
            </th>
            <th scope="col" class="px-4 py-3 cursor-pointer w-[10%] text-center" @click="sortJobs('jobtype')">Job Type
              <span :class="getSortClass('jobtype')"></span>
            </th>
            <th scope="col" class="px-4 py-3 cursor-pointer w-[5%] text-center" @click="sortJobs('time')">Time
              <span :class="getSortClass('time')"></span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 rounded-l-xl border-r">
          <template v-for="job in jobs" :key="job.id" class="border-b">
            <tr class="relative overflow-hidden">
              <td class="px-4 py-2">
                  <button :class="[
                    'inline-flex',
                    'mt-2',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-green-700`,
                    `bg-green-50`,
                    `ring-green-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-green-50/20`,
                    `dark:ring-green-400/75`,
                    `dark:text-green-300`
                  ]"> 
                        <a :href="`/job/${job.id}`"  target="_blank">#{{ job.id }}</a>
                  </button></br>
                  <button :class="[
                    'inline-flex',
                    'mt-2',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-blue-700`,
                    `bg-blue-50`,
                    `ring-blue-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-blue-50/20`,
                    `dark:ring-blue-400/75`,
                    `dark:text-blue-300`
                  ]" @click.stop="openJobHistory(job)">
                    <svg class="h-4 w-4 text-blue-500 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                    </svg>
                        History
                  </button>
              </td>
              <td class="px-4 py-2 text-center text-gray-800 dark:text-white space-y-3">
                <div class="flex items-center justify-center gap-1" title="Last Start">
                  <!-- Play icon for laststart -->
                  <svg class="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="10 8 16 12 10 16 10 8" />
                  </svg>
                  <span>{{ job.laststart || '--' }}</span>
                </div>

                <div class="flex items-center justify-center gap-1" title="Last Stop">
                  <!-- Pause icon for laststop -->
                  <svg class="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                  </svg>
                  <span>{{ job.laststop || '--' }}</span>
                </div>

                <div class="flex items-center justify-center gap-1" title="Last Touched">
                  <!-- Clock icon for ts (last touched) -->
                  <svg class="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                  </svg>
                  <span>{{ job.ts || '--' }}</span>
                </div>
              </td>
              <td class="px-4 py-2">{{ job.client?.companyname }}               
              </td>
              <td class="px-4 py-2">{{ getRequesterName(job) }}<br>
                      {{ formatPhone(getRequesterPhone(job)) }}<br>
                      {{ getRequesterEmail(job) }}             
              </td>
              <td class="px-4 py-2">
                <button v-if="job.ticket" :class="[
                  'inline-flex',
                  'mt-2',
                  'items-center',
                  'rounded-md',
                  'px-2',
                  'py-1',
                  'text-xs',
                  'font-medium',
                  `text-green-700`,
                  `bg-green-50`,
                  `ring-green-700/10`,
                  'ring-1',
                  'ring-inset',
                  `dark:bg-green-50/20`,
                  `dark:ring-green-400/75`,
                  `dark:text-green-300`
                ]"> 
                    <a :href="`/ticket/${job.ticket.id}`">#{{ job.ticket.id }}</a>
                </button>             
              </td>
              <td v-if="filters.userId == 'All'" class="px-4 py-2 text-center">{{ job.user_profile?.lastname }}, {{ job.user_profile?.firstname }}</td>
              <td
                class="px-4 py-2 bg-gray-50 dark:bg-gray-900 max-w-[750px] whitespace-normal break-words align-top"
              >
                <div v-if="shouldHideDescription(job)" class="italic text-red-500 text-sm">PRIVATE</div>
                <div v-else :id="`viewer-${job.id}`" class="h-[150px] overflow-y-auto"></div>
              </td>
              <td class="px-4 py-2" :class="['text-gray-900 dark:text-gray-200 h-full bg-opacity-40', job.background_class]"> 
                  {{ job.job_type?.name }}
              </td>
              <td class="px-4 py-2 text-center">{{ parseFloat(job.totaltime || 0).toFixed(2) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div class="text-right mt-4 font-bold dark:text-gray-200">Total Time: {{ totalTime.toFixed(2) }}</div>
    <JobHistoryModal :isVisible="showHistoryModal" :history= 'jobHistory' :jobId="selectedHistoryJobId" :hideDescription="hideSelectedJob" @close="showHistoryModal = false"/>
  </div>
</template>

<script>
import axios from 'axios';
import Viewer from '@toast-ui/editor/dist/toastui-editor-viewer';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import JobHistoryModal from '@/views/modals/JobHistoryModal.vue';
import ThemeService from '@/services/ThemeService';
export default {
  components: {
    Viewer,
    JobHistoryModal
  },
  name: 'UserJobs',
  data() {
    return {
      jobs: [],
      techs: [],
      clients: [],
      jobHistory: [],
      selectedHistoryJobId: null,
      showHistoryModal: false,
      totalTime: 0,
      hideSelectedJob: false,
      disableNext: false,
      filters: {
        userId: this.$route.params.userId,
        companyId: '',
        startDate: '',
        endDate: '',
        currentWeek: 'false',
        day: '',
      },
      companyname: '',
      dropdownState: {
        client: { index: -1, items: [], visible: false },
      },
      viewers: {},
      sortKey: '',
      sortOrder: 'asc',
      userId: parseInt(localStorage.getItem('user_id'), 10),
      userroles: [30]
    };
  },
  methods: {
    async fetchJobs() {
      if (!this.filters.userId) {
        return;
      }
      this.$updateLoading(1);

      try {
        const res = await axios.post('job/filtered/userjobs', {
          userId: this.filters.userId,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          companyId: this.filters.companyId,
          currentWeek: this.filters.currentWeek,
          day: this.filters.day
        });

        this.jobs = res.data.jobs;
        this.totalTime = res.data.totalTime;
        this.filters.startDate = res.data.startDate;
        this.filters.endDate = res.data.endDate;
        this.disableNext = res.data.disableNext;
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async openJobHistory(job) {
      this.$updateLoading(1);
      try {
        const response = await axios.get(`/job/history/${job.id}`);
        this.jobHistory = response.data;
        this.selectedHistoryJobId = job.id;
        this.hideSelectedJob = this.shouldHideDescription(job);
        this.showHistoryModal = true;
      } catch (error) {
        console.error("Error fetching job history:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    changeTech() {
      this.$router.push({ name: 'userJobsList', params: { userId: this.filters.userId } });
      this.fetchJobs();
    },
    sortJobs(column) {
      if (this.sortKey === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = column;
        this.sortOrder = 'asc';
      }

      this.jobs.sort((a, b) => {
        let aVal = a[column];
        let bVal = b[column];

        // handle nested values
        if (column === 'client') {
          aVal = a.client?.companyname || '';
          bVal = b.client?.companyname || '';
        } else if (column === 'jobtype') {
          aVal = a.job_type?.name || '';
          bVal = b.job_type?.name || '';
        } else if (column === 'time') {
          aVal = parseFloat(a.totaltime) || 0;
          bVal = parseFloat(b.totaltime) || 0;
        }

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    getRequesterName(job) {
      const c = job.contact;
      return c ? `${c.name_first || ''} ${c.name_last || ''}` : '';
    },
    cleanMarkdown(text) {
      return text?.replace(/[*_`#>~\-!]/g, '') || '';
    },
    changeDay(dir) {
      this.filters.day = dir;
      this.fetchJobs();
    },
    async fetchDropdowns() {
      axios.get('user/techs').then((res) => (this.techs = res.data));
      axios.get('clients').then((res) => (this.clients = res.data));
      axios.get(`user/role/${this.userId}`).then((res) => (this.userroles = res.data.roles));
    },
    onKeyDown(field, direction) {
      const state = this.dropdownState[field];
      if (!state.visible || state.items.length === 0) return;

      if (direction === 'down') {
        state.index = (state.index + 1) % state.items.length;
      } else if (direction === 'up') {
        state.index = (state.index - 1 + state.items.length) % state.items.length;
      }

      this.$nextTick(() => {
        const container = this.$el.querySelector(`.scrollable-requester-list[data-field="${field}"]`);
        const activeItem = container?.querySelector('.active-item');
        activeItem?.scrollIntoView({ block: 'nearest' });
      });
    },

    onEnter(field, selectFn, nextRef = null, ...args) {
      const state = this.dropdownState[field];
      if (state.index >= 0 && state.index < state.items.length) {
        selectFn(state.items[state.index], ...args);
        state.index = -1;
        if (nextRef) {
          this.$nextTick(() => this.$refs[nextRef]?.focus());
        }
      }
    },
    setClient (client) {
      this.filters.companyId = client.id;
      this.companyname = client.companyname;
      // searchReqTypeQuery.value = type.description;
      // this.showClientDropdown = false;
      this.dropdownState.client.visible = false;
      this.fetchJobs();
    },
    closeClientDropdown () {
      this.dropdownState.client.visible = false;
    },
    clearClient () {
      this.filters.companyId = '';
      this.companyname = '';
    },
    getSortClass(column) {
      if (this.sortKey === column) {
        return this.sortOrder === 'asc' ? 'sort-asc' : 'sort-desc';
      }
      return '';
    },
    initViewer(jobId, content) {
      this.$nextTick(() => {
        const el = document.getElementById(`viewer-${jobId}`);
        if (!el) return;

        if (this.viewers[jobId]) {
          this.viewers[jobId].destroy();
        }

        this.viewers[jobId] = new Viewer({
          el,
          initialValue: this.fixMarkdownIndentation(content || ''),
          theme: this.isdark ? 'dark' : 'default',
        });
      });
    },
    fixMarkdownIndentation(text) {
      return text
        .split('\n')
        .map(line => {
          // Fix indentation for list items that start with ' - '
          if (line.startsWith(' - ')) {
            line = '    - ' + line.slice(3);
          }

          // Fix markdown headings without space, e.g., '###Heading' → '### Heading'
          line = line.replace(/^(#{1,6})([^\s#])/g, '$1 $2');

          return line;
        })
        .join('\n');
    },
    shouldHideDescription(job) {
      const isPrivate = job.private == 1;
      const isUnpublishedTicket = job.ticket?.unpublished == 1;

      return (isPrivate || isUnpublishedTicket) && (parseInt(job.user_id) !== this.userId);
    },
    getRequesterPhone(job) {
      if (job.ticket && job.ticket.alternatephone) {
        return job.ticket.alternatephone;
      } else if (job.ticket && job.ticket.contact) {
        return job.ticket.contact.phone_primary || job.ticket.contact.phone_secondary || '';
      }
      return '';
    },
    getRequesterEmail(job) {
      if (job.ticket && job.ticket.email) {
        return job.ticket.email;
      } else if (job.ticket && job.ticket.contact && job.ticket.contact.email) {
        return job.ticket.contact.email;
      }
      return '';
    },
    formatPhone(phone) {
      if (!phone) return '';

      const raw = String(phone).trim();
      let digits = raw.replace(/\D/g, '');

      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }
      if (digits.length === 10) {
        const area = digits.slice(0, 3);
        const prefix = digits.slice(3, 6);
        const line = digits.slice(6);
        return `(${area}) ${prefix}-${line}`;
      }
      return raw;
    }
  },
  beforeUnmount() {
    Object.values(this.viewers).forEach(viewer => {
      if (viewer) viewer.destroy();
    });
  },
  watch: {
    jobs(newJobs) {
      newJobs.forEach(job => {
        this.$nextTick(() => {
          this.initViewer(job.id, job.description);
        });
      });
    },
    isdark(newVal) {
      console.log('heyyyyyy (dark mode change detected): ', this.isdark);
      this.$nextTick(() => {
        this.jobs.forEach(job => {
          const viewerContainerId = `viewer-${job.id}`;
          const container = document.getElementById(viewerContainerId);
          if (!container) {
            console.warn('Viewer container not found:', viewerContainerId);
            return;
          }
          if (this.viewers[viewerContainerId]) {
            this.viewers[viewerContainerId].destroy();
            delete this.viewers[viewerContainerId];
          }
          container.innerHTML = '';

          const newEl = document.createElement('div');
          newEl.id = viewerContainerId;
          container.parentNode.replaceChild(newEl, container);
          this.initViewer(job.id, job.description);
        });
      });
    }
  },
  computed: {
    isdark() {
      return ThemeService.isDark.value;
    },
    groupedTechnicians() {
      const groups = {};
      const ungrouped = [];

      this.techs.forEach(tech => {
        if (!tech.deptId) {
          // No deptId at all → push to ungrouped
          ungrouped.push({
            id: tech.id,
            name: tech.name,
          });
          return;
        }

        let groupKey = '';

        if (tech.deptId === "External Tech") {
          groupKey = "External Tech";
        } else if (tech.details) {
          groupKey = tech.details;
        } else {
          groupKey = "Other Department";
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }

        // Append company name for external techs
        let displayName = tech.name;
        if (tech.deptId === "External Tech" && tech.details) {
          displayName += ` (${tech.details})`;
        }

        groups[groupKey].push({
          id: tech.id,
          name: displayName,
        });
      });

      // If ungrouped exist, add as a group at the top
      if (ungrouped.length > 0) {
        groups[""] = ungrouped; // Empty string so it doesn't show a group label
      }

      // Get keys and sort
      let keys = Object.keys(groups).filter(k => k !== "External Tech").sort((a, b) => {
        if (a === "") return -1; // Keep ungrouped first
        if (b === "") return 1;
        return a.localeCompare(b);
      });

      // Add External Tech last
      if (groups["External Tech"]) {
        keys.push("External Tech");
      }

      // Rebuild ordered groups object
      const orderedGroups = {};
      keys.forEach(key => {
        orderedGroups[key] = groups[key];
      });

      return orderedGroups;
    },
    filteredClients () {
      const filtered = this.companyname
        ? this.clients.filter((e) =>
          e.companyname
            .toLowerCase()
            .includes(this.companyname.toLowerCase())
        )
        : this.clients;
      this.dropdownState.client.items = filtered;
      return filtered;
    }
  },
  async mounted() {
    await this.fetchDropdowns();
    this.fetchJobs();
  },
};
</script>

<style scoped>
.scrollable-requester-list {
max-height: 400px;
/* Adjust this value based on your design needs */
overflow-y: auto;
/* Enables vertical scrolling */
border: 1px solid #ccc;
/* Optional: adds a border around the list */
}
table td {
  vertical-align: top !important;
  /* padding-top: 20px; */
}

.sort-asc::after {
  content: '▲';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.sort-desc::after {
  content: '▼';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}
</style>
  